"use client";

import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { Plus, MoreVertical, Edit, Trash2 } from "lucide-react";
import LoadingSpinner from "@/components/loading-spinner";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-and-permissions-context";
import { PriceModifierFormDialog } from "./price-modifier-form-dialog";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { ROLES } from "@/lib/constants/roles";

interface PriceModifier {
  id: number;
  venue_id: number;
  name: string;
  start_date: string;
  end_date: string;
  modifier_type: "discount" | "surcharge";
  modifier_value: number;
  status_id: number;
  status?: {
    id: number;
    name: string;
    type: string;
  };
  created_at: string;
  updated_at: string;
  price_points?: number[] | null;
}

interface PriceModifierListProps {
  venueId: number;
}

type FilterStatus = "all" | number; // Use number to allow any status ID

export function PriceModifierList({ venueId }: PriceModifierListProps) {
  const { user, isLoading: userLoading } = useAuth();
  const [priceModifiers, setPriceModifiers] = useState<PriceModifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedModifier, setSelectedModifier] =
    useState<PriceModifier | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modifierToDelete, setModifierToDelete] =
    useState<PriceModifier | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isSuperAdmin = user?.role_id === ROLES.SUPER_ADMIN;
  const isBookingStaff = user?.role_id === ROLES.BOOKING_STAFF;
  const canCreate = isSuperAdmin || isBookingStaff; // Allow both super admin and booking staff to create
  const canDelete = isSuperAdmin;
  const canEdit = true; // Both super admin and booking admin can edit

  useEffect(() => {
    fetchPriceModifiers();
  }, [venueId]);

  const fetchPriceModifiers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/venues/${venueId}/price-modifiers`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error("API Error:", response.status, errorData);
        throw new Error(errorData?.error || "Failed to fetch price modifiers");
      }

      const data = await response.json();
      setPriceModifiers(data.priceModifiers || []);
    } catch (error: any) {
      console.error("Error fetching price modifiers:", error);
      toast.error(error.message || "Failed to load price modifiers");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (modifier: PriceModifier) => {
    setModifierToDelete(modifier);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setModifierToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!modifierToDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(
        `/api/venues/${venueId}/price-modifiers/${modifierToDelete.id}`,
        { method: "DELETE" },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete price modifier");
      }

      toast.success("Price modifier deleted successfully");
      await fetchPriceModifiers();
      setDeleteDialogOpen(false);
      setModifierToDelete(null);
    } catch (error) {
      console.error("Error deleting price modifier:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to delete price modifier",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (modifier: PriceModifier) => {
    setSelectedModifier(modifier);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedModifier(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedModifier(null);
    fetchPriceModifiers();
  };

  const formatValue = (modifier: PriceModifier) => {
    // Display with appropriate sign
    return modifier.modifier_type === "discount"
      ? `-${modifier.modifier_value}%`
      : `+${modifier.modifier_value}%`;
  };

  const getModifierStatus = (modifier: PriceModifier): FilterStatus => {
    if (modifier.status_id === STATUS_IDS.PRICE_MODIFIER_ACTIVE)
      return STATUS_IDS.PRICE_MODIFIER_ACTIVE;
    if (modifier.status_id === STATUS_IDS.PRICE_MODIFIER_INACTIVE)
      return STATUS_IDS.PRICE_MODIFIER_INACTIVE;
    if (modifier.status_id === STATUS_IDS.PRICE_MODIFIER_SCHEDULED)
      return STATUS_IDS.PRICE_MODIFIER_ACTIVE; // Treat scheduled as active
    if (modifier.status_id === STATUS_IDS.PRICE_MODIFIER_ARCHIVED)
      return STATUS_IDS.PRICE_MODIFIER_ARCHIVED;
    return modifier.status_id;
  };

  const getStatusBadge = (statusId: number) => {
    let className = "";
    let text = "";

    switch (statusId) {
      case STATUS_IDS.PRICE_MODIFIER_ACTIVE:
        className = "bg-green-100 text-green-800 border-green-200";
        text = "Active";
        break;
      case STATUS_IDS.PRICE_MODIFIER_INACTIVE:
        className = "bg-red-100 text-red-800 border-red-200";
        text = "Inactive";
        break;
      case STATUS_IDS.PRICE_MODIFIER_SCHEDULED:
        className = "bg-blue-100 text-blue-800 border-blue-200";
        text = "Scheduled";
        break;
      case STATUS_IDS.PRICE_MODIFIER_ARCHIVED:
        className = "bg-gray-100 text-gray-800 border-gray-200";
        text = "Archived";
        break;
      default:
        return null;
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${className}`}
      >
        {text}
      </span>
    );
  };

  // Filter modifiers based on status
  const filteredModifiers = priceModifiers.filter((modifier) => {
    if (filterStatus === "all") return true;
    // Include scheduled modifiers when filtering by active
    if (filterStatus === STATUS_IDS.PRICE_MODIFIER_ACTIVE) {
      return (
        modifier.status_id === STATUS_IDS.PRICE_MODIFIER_ACTIVE ||
        modifier.status_id === STATUS_IDS.PRICE_MODIFIER_SCHEDULED
      );
    }
    return modifier.status_id === filterStatus;
  });

  // Count modifiers by status (combine scheduled with active)
  const statusCounts = {
    all: priceModifiers.length,
    [STATUS_IDS.PRICE_MODIFIER_ACTIVE]: priceModifiers.filter(
      (m) =>
        m.status_id === STATUS_IDS.PRICE_MODIFIER_ACTIVE ||
        m.status_id === STATUS_IDS.PRICE_MODIFIER_SCHEDULED,
    ).length,
    [STATUS_IDS.PRICE_MODIFIER_INACTIVE]: priceModifiers.filter(
      (m) => m.status_id === STATUS_IDS.PRICE_MODIFIER_INACTIVE,
    ).length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Price modifiers</h1>
          <p className="text-gray-600 mt-2">
            Manage discounts, surcharges, and special pricing
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Add price modifier
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by status
          </label>
          <div className="flex flex-wrap gap-2">
            {(
              [
                "all",
                STATUS_IDS.PRICE_MODIFIER_ACTIVE,
                STATUS_IDS.PRICE_MODIFIER_INACTIVE,
              ] as FilterStatus[]
            ).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? "bg-primary text-primary-foreground"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {status === "all"
                  ? "All"
                  : status === STATUS_IDS.PRICE_MODIFIER_ACTIVE
                    ? "Active"
                    : status === STATUS_IDS.PRICE_MODIFIER_INACTIVE
                      ? "Inactive"
                      : ""}{" "}
                ({statusCounts[status as keyof typeof statusCounts]})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Price Modifiers Table */}
      {filteredModifiers.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow className="border-b-2">
                <TableHead className="text-gray-700 font-semibold w-1/4 px-6 py-4">
                  Name
                </TableHead>
                <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                  Value
                </TableHead>
                <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                  From
                </TableHead>
                <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                  To
                </TableHead>
                <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                  Price points
                </TableHead>
                <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                  Status
                </TableHead>
                <TableHead className="text-right text-gray-700 font-semibold w-1/8 px-6 py-4">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredModifiers.map((modifier) => {
                const status = getModifierStatus(modifier);
                const pricePointCount = modifier.price_points?.length || 0;

                return (
                  <TableRow key={modifier.id} className="hover:bg-gray-50">
                    <TableCell className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {modifier.name}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <span className="text-gray-700 font-medium">
                        {formatValue(modifier)}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <span className="text-gray-700">
                        {format(new Date(modifier.start_date), "M/d/yyyy")}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <span className="text-gray-700">
                        {format(new Date(modifier.end_date), "M/d/yyyy")}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {pricePointCount > 0 ? (
                        <span className="text-gray-700">
                          {pricePointCount} selected
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {getStatusBadge(modifier.status_id)}
                    </TableCell>
                    <TableCell className="text-right px-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (
                            <DropdownMenuItem
                              onClick={() => handleEdit(modifier)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(modifier)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-gray-400 text-6xl mb-4">💰</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No price modifiers found
          </h3>
          <p className="text-gray-600 mb-4">
            Create price modifiers to manage discounts and surcharges.
          </p>
          {canCreate && (
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add price modifier
            </Button>
          )}
        </div>
      )}

      <PriceModifierFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        venueId={venueId}
        modifier={selectedModifier}
        onSuccess={handleDialogClose}
      />

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete price modifier"
        description="Are you sure you want to delete this price modifier?"
        itemName={modifierToDelete?.name}
        confirmText="Delete"
        confirmLoadingText="Deleting..."
        isLoading={isDeleting}
      />
    </>
  );
}
